/***
 * Add to cart form component
 * 
 * 用法1：直接加购（单变体或已知 variant ID）
 * <add-to-cart-from
 *   class="btn"
 *   data-product-info='{"id":"{{ variant.id }}", "quantity":"1"}'
 * >
 *   <span class="add__cart-text">Add to cart</span>
 *   <div class="loading-overlay__spinner hidden">
 *     {%- render 'icon-cart-loading' -%}
 *   </div>
 * </add-to-cart-from>
 * 
 * 用法2：打开选择弹窗（多变体产品）
 * <add-to-cart-from
 *   class="btn"
 *   data-product-url="{{ product.url }}"
 * >
 *   <span class="add__cart-text">Quick Add</span>
 *   <div class="loading-overlay__spinner hidden">
 *     {%- render 'icon-cart-loading' -%}
 *   </div>
 * </add-to-cart-from>
 * 
 * 属性说明：
 * - data-product-info: 商品变体信息（JSON格式），包含 id 和 quantity
 * - data-product-url: 商品页面 URL，用于获取完整的变体选择界面（首次点击时按需加载 quick-add / product-form / drawer 样式，URL 来自 theme.liquid 的 window.themeQuickAddDeps）
 * - 两个属性二选一，优先使用 data-product-info
 */

class AddToCartFrom extends HTMLElement {
  /** @type {Promise<void>|null} */
  static _quickAddDepsPromise = null;

  /**
   * Loads drawer CSS + quick-add.js + product-form.js once (skips if already on the page).
   * URLs come from window.themeQuickAddDeps (set in theme.liquid).
   */
  static ensureQuickAddDependencies() {
    const deps = window.themeQuickAddDeps;
    if (!deps?.quickAddJs || !deps?.productFormJs) {
      return Promise.reject(new Error('Quick add assets are not configured'));
    }

    if (AddToCartFrom._quickAddDepsPromise) {
      return AddToCartFrom._quickAddDepsPromise;
    }
    
    AddToCartFrom._quickAddDepsPromise = (async () => {
      if (deps.drawerCss && !document.querySelector('link[data-atc-quick-add-drawer-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = deps.drawerCss;
        link.media = 'print';
        link.onload = () => {
          link.media = 'all';
        };
        link.setAttribute('data-atc-quick-add-drawer-css', '');
        document.head.appendChild(link);
      }

      await AddToCartFrom._loadScriptOnce(deps.quickAddJs, 'quick-add.js');
      await customElements.whenDefined('quick-add-drawer');

      await AddToCartFrom._loadScriptOnce(deps.productFormJs, 'product-form.js');
      await customElements.whenDefined('product-form');
    })().catch((err) => {
      AddToCartFrom._quickAddDepsPromise = null;
      throw err;
    });

    return AddToCartFrom._quickAddDepsPromise;
  }

  /**
   * @param {string} url
   * @param {string} basename e.g. quick-add.js
   */
  static _loadScriptOnce(url, basename) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const ok = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      const bad = () => {
        if (!settled) {
          settled = true;
          reject(new Error(`Failed to load ${basename}`));
        }
      };

      const existing = Array.from(document.scripts).find((s) => s.src.includes(basename));
      if (existing) {
        const checkReady = () => {
          if (basename === 'quick-add.js' && customElements.get('quick-add-drawer')) {
            ok();
            return true;
          }
          if (basename === 'product-form.js' && customElements.get('product-form')) {
            ok();
            return true;
          }
          return false;
        };
        if (checkReady()) return;
        existing.addEventListener('load', ok, { once: true });
        existing.addEventListener('error', bad, { once: true });
        queueMicrotask(() => checkReady());
        return;
      }

      const s = document.createElement('script');
      s.src = url;
      s.defer = true;
      s.onload = ok;
      s.onerror = bad;
      document.head.appendChild(s);
    });
  }

  /** Same as QuickAddDrawer.reInjectScript — used when quick-add.js not loaded yet or CE not upgraded */
  static reinjectScriptsInElement(element) {
    if (!element || typeof element.querySelectorAll !== 'function') return;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  constructor() {
    super();
    this.init();
  }

  init() {
    // 组件本身就是按钮，不需要再查找子元素
    // 初始化各种必要的属性
    this.initProperties();
    // 准备请求数据（只有在有 productInfo 时才准备）
    if (this.productInfo) {
      this.prepareRequestData();
    }
    // 添加事件监听器
    this.addEventListener('click', this.handleAddToCartClick.bind(this));
  }

  /**
   * 初始化组件属性
   */
  initProperties() {
    this.addCartText = this.querySelector('.add__cart-text');
    this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.productInfo = this.getAttribute('data-product-info');
    this.productUrl = this.getAttribute('data-product-url');
    this.cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';

    // 只有在有 productInfo 时才格式化商品数据
    if (this.productInfo) {
      this.formattedItems = this.formatProductItems();
    }
  }



  /**
   * 格式化商品数据
   * @returns {Array} 格式化后的商品数据
   */
  formatProductItems() {
    try {
      let productItems = Array.isArray(this.productInfo) ?
        JSON.parse(this.productInfo) : [JSON.parse(this.productInfo)];

      return productItems.map(item => ({
        quantity: item.quantity || 1,
        id: parseInt(item.variantId || item.id)
      }));
    } catch (error) {
      console.error('商品数据格式化失败:', error);
      return [];
    }
  }

  /**
   * 获取fetchConfig配置
   * @param {string} type - 请求类型
   * @returns {Object} 请求配置对象
   */
  getFetchConfig(type = 'json') {
    if (typeof window.fetchConfig === 'function') {
      return window.fetchConfig(type);
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': type === 'json' ? 'application/json' : 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
  }

  /**
   * 准备请求数据
   */
  prepareRequestData() {
    // 构建基础配置
    this.config = this.getFetchConfig('json');

    // 准备请求数据
    let requestData = {
      items: this.formattedItems
    };

    // 添加sections数据（如果有购物车抽屉）
    this.addSectionsData(requestData);

    // 转换为JSON
    this.config.body = JSON.stringify(requestData);
  }

  /**
   * 添加购物车sections数据
   * @param {Object} requestData - 请求数据对象
   */
  addSectionsData(requestData) {
    if (!this.cartDrawer || typeof this.cartDrawer.getSectionsToRender !== 'function') {
      return;
    }

    try {
      const sections = this.cartDrawer.getSectionsToRender().map(
        section => section.id || section.section || section
      );
      requestData.sections = sections;
      requestData.sections_url = window.location.pathname;
    } catch (error) {
      console.error('Error getting sections:', error);
    }
  }

  /**
   * 处理添加到购物车点击事件
   * @param {Event} event - 点击事件
   */
  handleAddToCartClick(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      // 判断是直接加购还是打开选择弹窗
      if (this.productInfo) {
        // 有 variant ID，直接加购
        this.showLoadingState();
        this.addToCart();
      } else if (this.productUrl) {
        // 只有 product URL，打开选择弹窗
        this.openQuickAddDrawer();
      } else {
        console.error('需要提供 data-product-info 或 data-product-url');
        this.showErrorMessage('缺少必要的产品信息');
      }
    } catch (error) {
      console.error('Error processing add to cart:', error);
      this.hideLoadingState();
      this.showErrorMessage(error.message);
    }
  }

  /**
   * 显示加载状态
   */
  showLoadingState() {
    if (this.addCartText) this.addCartText.classList.add('hidden');
    if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');
  }

  /**
   * 隐藏加载状态
   */
  hideLoadingState() {
    if (this.addCartText) this.addCartText.classList.remove('hidden');
    if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
  }

  /**
   * 添加商品到购物车
   */
  addToCart() {
    fetch(this.cartAddUrl, this.config)
      .then(this.handleResponse.bind(this))
      .then(this.handleSuccess.bind(this))
      .catch(this.handleError.bind(this))
      .finally(this.ensureCartNotEmpty.bind(this));
  }

  /**
   * 处理API响应
   * @param {Response} response - fetch请求响应
   * @returns {Promise} 解析的JSON数据
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || `服务器返回错误: ${response.status}`);
    }
    return response.json();
  }

  /**
   * 处理成功添加到购物车
   * @param {Object} data - 响应数据
   */
  handleSuccess(data) {
    console.log('Items added successfully:', data);
    this.hideLoadingState();
    this.updateCartDrawer(data);
    this.showSuccessMessage();
  }

  /**
   * 更新购物车抽屉
   * @param {Object} data - 购物车数据
   */
  updateCartDrawer(data) {
    if (!this.cartDrawer) return;

    // 更新购物车内容
    if (typeof this.cartDrawer.renderContents === 'function') {
      this.cartDrawer.renderContents(data);
    }

    // 打开交给 renderContents → cart-drawer.open()。
    // 那边用 body.open-drawer-fixed 锁滚动，close() 会解开。
    // 不要再加 overflow-hidden：关闭抽屉时没人移除它，页面会一直滚不动。
    this.cartDrawer.classList.add('animate', 'active');
  }

  /**
   * 显示成功消息
   */
  showSuccessMessage() {
    if (window.pushSuccessMessage) {
      window.pushSuccessMessage(window.cartStrings?.success || '商品已添加到购物车');
    }
  }

  /**
   * 处理错误
   * @param {Error} error - 错误对象
   */
  handleError(error) {
    console.error('Error adding items to cart:', error);
    this.hideLoadingState();
    this.showErrorMessage(error.message);
  }

  /**
   * 显示错误消息
   * @param {string} message - 错误消息
   */
  showErrorMessage(message) {
    if (window.pushErrorMessage) {
      window.pushErrorMessage(message || '添加商品失败，请稍后重试');
    }
  }

  /**
   * 确保购物车不显示为空
   */
  ensureCartNotEmpty() {
    if (this.cartDrawer && this.cartDrawer.classList.contains('is-empty')) {
      this.cartDrawer.classList.remove('is-empty');
    }
  }

  /**
   * 打开快速添加弹窗（用于多变体产品）
   */
  openQuickAddDrawer() {
    this.showLoadingState();

    AddToCartFrom.ensureQuickAddDependencies()
      .then(() => {
        let url = this.productUrl;
        if (url.includes('?')) {
          url += '&';
        } else {
          url += '?';
        }
        url += 'section_id=quick-add-drawer';

        return fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const newQuickAddDrawer = getDomHtmlFromText(responseText, 'quick-add-drawer');

            if (!newQuickAddDrawer) {
              throw new Error('未找到 quick-add-drawer 元素');
            }

            const opener = this;

            const mountFirstDrawer = () => {
              document.body.appendChild(newQuickAddDrawer);
              if (typeof newQuickAddDrawer.reInjectScript === 'function') {
                newQuickAddDrawer.reInjectScript(newQuickAddDrawer);
              } else {
                AddToCartFrom.reinjectScriptsInElement(newQuickAddDrawer);
              }
              setTimeout(() => {
                if (typeof newQuickAddDrawer.open === 'function') {
                  newQuickAddDrawer.open(opener);
                } else {
                  opener.showErrorMessage('加载产品详情失败');
                }
              }, 300);
              window.hasQuickAddDrawer = true;
            };

            if (window.hasQuickAddDrawer) {
              const quickAddDrawer = document.querySelector('quick-add-drawer');
              const mainProduct = newQuickAddDrawer.querySelector('.quick-add-main-product');
              if (quickAddDrawer && mainProduct) {
                if (typeof quickAddDrawer.setInnerHTML === 'function') {
                  quickAddDrawer.setInnerHTML(mainProduct.innerHTML);
                } else {
                  const box = quickAddDrawer.querySelector('.quick-add-main-product');
                  if (box) box.innerHTML = mainProduct.innerHTML;
                  AddToCartFrom.reinjectScriptsInElement(quickAddDrawer);
                }
                if (typeof quickAddDrawer.open === 'function') {
                  quickAddDrawer.open(this);
                }
              }
            } else if (customElements.get('quick-add-drawer')) {
              mountFirstDrawer();
            } else {
              customElements.whenDefined('quick-add-drawer').then(mountFirstDrawer).catch(() => {
                opener.showErrorMessage('加载产品详情失败');
              });
            }
          });
      })
      .catch((error) => {
        console.error('Error loading quick add or product details:', error);
        const msg =
          error && String(error.message || '').includes('Quick add')
            ? '无法加载快速加购组件'
            : '加载产品详情失败';
        this.showErrorMessage(msg);
      })
      .finally(() => {
        this.hideLoadingState();
      });
  }
}

customElements.define('add-to-cart-from', AddToCartFrom);
